import React, { useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import Slider from "../../../../components/slider";
import Wizard from "../../../../components/wizard";
import { Input } from "../../../../components/input";
import { AssetViewerSelector } from "src/pages/collections/components/create"
import {
  DEALROOM_ASSET_VIEWERS,
  DEAL_ROOM_EXPERIENCE,
  DEFAULT_ASSET_VIEWER,
} from "../../../../constants/collections";

import MapDeals from "./mapDeals";
import Button from "../../../../components/button";
import Reveal from "../../../../components/animation";
import subscriptionPlanService from "../../../../utils/subscriptionPlan.service";
import { createCollection } from "../../../../redux/actions/collections";

import { Collection } from "../../../../types/collections";

import _ from "lodash";
import cx from "classnames";
import styles from "./styles.module.scss";
import { useCollectionsAdoption } from "src/redux/actions/adoption/collection";
import { createDealRoom, invalidateDealRoomCreationState } from "src/redux/actions/dealRoom";
import { fetchIntegrations } from "src/legacy/redux/actions/integrations";
import { useDealRoomAdoption } from "src/redux/actions/adoption/dealRoom";
import { DEAL_ROOM, DEAL_ROOMS_LIST } from "src/constants/adoption/sourceTypes";
import { IUseFetchProps, useFetch } from "src/components/hooks/useFetch";
interface CreateCollectionDialogProps {
  show: boolean;
  list?: string | undefined;
  onClose: () => void;
}

const CreateDealRoomDialog: React.FC<CreateCollectionDialogProps> = ({
  show,
  list,
  onClose,
}) => {
  const dispatch : any = useDispatch();
  const navigate = useNavigate();

  const { trackDealRoomCreateViewed, trackDealRoomCreated } = useDealRoomAdoption();

  const activatedIntegrations = useSelector((state) => _.get(state, 'legacyRootReducer.settings.integrations.activated.items', []));
  const crmIntegrations = _.filter(activatedIntegrations, (item: any) => {
    return _.get(item, "category.name") === "CRM" && _.get(item, "state.code", "") === "ok";
  });
  const isCRMIntegrated = _.size(crmIntegrations) > 0;

  const [currentStep, setCurrentStep] = React.useState(0);
  const [name, setName] = React.useState("");
  const [selectedOpportunity, setSelectedOpportunity] = React.useState<string>("");
  const [assetViewer, setAssetViewer] = React.useState(DEFAULT_ASSET_VIEWER);
  const [error, setError] = React.useState<string | null>(null);
  const defaultDescription = "<p>Welcome to your deal room, this is the first screen your audience sees when they enter. Add in a description to help set the context for this space. Keep it crisp and clear! Using placeholders like asset count, first name, last name and brand colours can further personalise your buyer’s journey.</p>"
  const dealRoomDescription = useSelector((state) => _.get(state, `accounts.item.dealRoomSettings.description`,""));
  const { trackViewerTypeSelectedEvent } = useCollectionsAdoption();

  const creating = useSelector(
    (state) => _.get(state, `dealRooms.creation.creating`) || false,
  );
  const isDealRoomCreated =
    useSelector((state) => _.get(state, "dealRooms.creation.created")) || false;
  const createdDealRoom = useSelector((state) => _.get(state, `dealRooms.creation.item.id`));

  const createDealRoomProps: IUseFetchProps = {
    action: createDealRoom,
    loading: creating,
    succeeded: isDealRoomCreated,
    response: createdDealRoom as any,
    error: null,
  }

  const { handleFetch: createDealRoomRequest } = useFetch(createDealRoomProps);

  const assetViewers = useMemo(() => {
    return subscriptionPlanService
      .getCollectionAssetViewers()
      .map((viewerId) => {
        return DEALROOM_ASSET_VIEWERS.find((item) => item.id === viewerId);
      })
      .filter((item) => item);
  }, [subscriptionPlanService.getCollectionAssetViewers()]);
  
  const nextButtonText = useMemo(() => {
    if (currentStep === 1) {
      if (creating) {
        return "Creating...";
      }

      if(!_.isEmpty(selectedOpportunity)) {
        return "Create"
      }

      return "Skip and Create";
    }

    return "Next";
  }, [creating, currentStep, selectedOpportunity]);

  const createdCollection = useSelector((state) => {
    const finished = _.get(state, `dealRooms.creation.created`) || false;
    if (finished) {
      return _.get(state, `dealRooms.creation.item.id`) as unknown as Collection;
    }

    return null;
  });

  React.useEffect(() => {
    if (!show) return;

    trackDealRoomCreateViewed({ source_cta: DEAL_ROOMS_LIST });
  }, [show]);

  React.useEffect(() => {
    if (createdCollection) {
      navigate(`/deal-room/${createdCollection}/overview?edit=true`);
      dispatch(invalidateDealRoomCreationState());
      dispatch(fetchIntegrations());
      onClose();
    }
  }, [createdCollection]);

  const handleClose = () => {
    onClose();
  };

  const handleStepChange = (step: number) => {
    setCurrentStep(step);
  };

  const handleNameChange = (e) => {
    setName(e.target.value);
    setError(null);
  };

  const handleAssetViewerChange = (viewer: any) => {
    setAssetViewer(viewer);

    trackViewerTypeSelectedEvent({
      viewer: viewer,
    });
  };

  const handleConfirm = () => {
    const e = validateForm();
    if (e) {
      setError(e);
      return;
    }

    goToNextStep(currentStep);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleConfirm();
  };

  const goToNextStep = (step) => {
    if (step === 1) {
      handleCreateCollection();
    } else {
      setCurrentStep(step + 1);
    }
  };

  const handleCreateCollection = () => {
    const request = {
      name: name,
      description: _.size(dealRoomDescription) > 0 ? dealRoomDescription : defaultDescription,
    };

    if (_.size(selectedOpportunity) > 0) {
      request["opportunity"] = selectedOpportunity;
    }

    createDealRoomRequest(request)
      .then(() => trackDealRoomCreated({ dealRoomName: name }));
  };

  const handleCancel = () => {
    setName("");
    setError(null);
    setCurrentStep(0);
    onClose();
  };

  const validateForm = () => {
    if (_.isEmpty(name) || _.isEmpty(_.trim(name))) {
      return "Please provide a valid name";
    }

    return null;
  };

  return (
    <Slider.Dialog
      className={styles.createCollectionDialog}
      width={"380"}
      show={show}
      onClose={handleClose}
    >
      <Slider.Header
        className={styles.header}
        title={
          <>
            creating new <br />
            deal room
          </>
        }
      />
      <Slider.Body className={styles.body}>
        <Wizard.Pane
          className={styles.wizard}
          currentStep={currentStep}
          gap={30}
          onStepChange={handleStepChange}
        >
          <Wizard.Step name="Name" completed={!(_.isEmpty(name) || _.isEmpty(_.trim(name)))}>
            <form
              className={cx(styles.step, styles.nameStep)}
              onSubmit={handleSubmit}
            >
              <Input
                autoFocus
                className={styles.input}
                placeholder="Give your deal room a name"
                value={name}
                onChange={handleNameChange}
              />
              <Reveal show={!!error}>
                <div className={styles.error}>
                  <i className="fa-solid fa-triangle-exclamation"></i>
                  {error}
                </div>
              </Reveal>

              <p style={{ marginTop: 20 }}>
                A shared workspace to collaborate with your team and engage buyers
              </p>
      
            </form>
          </Wizard.Step>
          <Wizard.Step name="Map Your Deal" completed={true}>
            <div className={cx(styles.step, styles.experienceStep)}>
              <MapDeals isCRMIntegrated={isCRMIntegrated} setSelectedOpportunity={setSelectedOpportunity} selectedOpportunity={selectedOpportunity} />
            </div>
          </Wizard.Step>
        </Wizard.Pane>
      </Slider.Body>
      <Slider.Actions>
        <Button
          icon="paperflite-tick"
          text={nextButtonText}
          variant="primary"
          disabled={creating}
          onClick={handleConfirm}
          className={styles.confirmButton}
        />
        <Button
          text="Cancel"
          variant="secondary"
          disabled={creating}
          onClick={handleCancel}
        />
      </Slider.Actions>
    </Slider.Dialog>
  );
};


export { CreateDealRoomDialog };
